import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Eye, EyeOff, CheckCircle2, ArrowLeft } from 'lucide-react';
import { loginSuccess } from '../../features/auth/authSlice';
import { setWishlist } from '../../features/wishlist/wishlistSlice';
import { getWishlist, addToWishlist } from '../../services/wishlistService';
import api from '../../services/api';
import { isAnalyticsAllowed } from '../../utils/analyticsConsent';
import { useLocale } from '../../contexts/LocaleContext';
import RegisterInterestStep from '../../components/ALLJsx/RegisterInterestStep';
import '../AllCss/Auth.css';
import BrandLogo from '../../components/ALLJsx/BrandLogo';

function Auth() {
  const dispatch = useDispatch();
  const { t } = useLocale();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const [step, setStep] = useState('email');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [keepSignedIn, setKeepSignedIn] = useState(false);
  const [requiresTwoFactor, setRequiresTwoFactor] = useState(false);
  const [twoFactorCode, setTwoFactorCode] = useState('');

  const redirectPath = searchParams.get('redirect') || '';

  const [interestType, setInterestType] = useState('');
  const [addressFields, setAddressFields] = useState({
    address: '',
    address2: '',
    city: '',
    state: '',
    zipCode: '',
  });
  const [interestFields, setInterestFields] = useState({
    budgetMax: '',
    interestMake: '',
    interestModel: '',
    interestYearMin: '',
    interestYearMax: '',
    interestMileageMax: '',
    sellYear: '',
    sellMake: '',
    sellModel: '',
    sellMileage: '',
    sellCondition: 'used',
  });

  const validatePassword = (pwd) => {
    const minLength = pwd.length >= 8;
    const hasNumber = /\d/.test(pwd);
    const hasLetter = /[a-zA-Z]/.test(pwd);
    const noSpaces = !/\s/.test(pwd);
    
    return {
      isValid: minLength && hasNumber && hasLetter && noSpaces,
      minLength,
      hasNumber,
      hasLetter,
      noSpaces,
    };
  };

  const validateEmail = (emailToValidate) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(emailToValidate);
  };

  const formatPhoneNumber = (value) => {
    const phoneNumber = value.replace(/\D/g, '');
    if (phoneNumber.length <= 3) return phoneNumber;
    if (phoneNumber.length <= 6) return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    setPhone(formatted);
  };

  const precheckEmailFlow = async (emailToCheck) => {
    try {
      const response = await api.post('/auth/check-email', { email: emailToCheck });
      if (response.data?.success !== true) return null;
      return response.data?.data?.continueTo ?? 'login';
    } catch (err) {
      console.error('Error checking email:', err);
      setError('Unable to verify email. Please try again.');
      return null;
    }
  };

  const handleEmailContinue = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      setLoading(false);
      return;
    }

    try {
      const continueTo = await precheckEmailFlow(email);
      if (continueTo === 'register') {
        setPassword('');
        setRequiresTwoFactor(false);
        setTwoFactorCode('');
        setStep('register');
      } else if (continueTo === 'login') {
        setStep('login');
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterContinue = (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const passwordValidation = validatePassword(password);
    if (!passwordValidation.isValid) {
      setError('Password does not meet requirements');
      setLoading(false);
      return;
    }

    const phoneDigits = phone.replace(/\D/g, '');
    if (phoneDigits.length !== 10) {
      setError('Please enter a valid 10-digit phone number');
      setLoading(false);
      return;
    }

    if (!firstName.trim() || !lastName.trim()) {
      setError('First and last name are required');
      setLoading(false);
      return;
    }

    setLoading(false);
    setStep('register-details');
  };

  const finishRegistration = async (includeInterest) => {
    setError('');
    setLoading(true);

    if (includeInterest && interestType !== 'purchase' && interestType !== 'sell') {
      setError('Please choose whether you want to buy a vehicle or sell/trade.');
      setLoading(false);
      return;
    }

    const phoneDigits = phone.replace(/\D/g, '');

    const payload = {
      email,
      password,
      fullName: `${firstName.trim()} ${lastName.trim()}`,
      phone: phoneDigits,
      address: addressFields.address.trim() || undefined,
      address2: addressFields.address2.trim() || undefined,
      city: addressFields.city.trim() || undefined,
      state: addressFields.state || undefined,
      zipCode: addressFields.zipCode.trim() || undefined,
    };

    if (includeInterest) {
      payload.interestType = interestType;
      if (interestType === 'purchase') {
        payload.budgetMax = interestFields.budgetMax
          ? Number(String(interestFields.budgetMax).replace(/\D/g, ''))
          : undefined;
        payload.interestMake = interestFields.interestMake || undefined;
        payload.interestModel = interestFields.interestModel || undefined;
        payload.interestYearMin = interestFields.interestYearMin
          ? Number(interestFields.interestYearMin)
          : undefined;
        payload.interestYearMax = interestFields.interestYearMax
          ? Number(interestFields.interestYearMax)
          : undefined;
        payload.interestMileageMax = interestFields.interestMileageMax
          ? Number(interestFields.interestMileageMax)
          : undefined;
      } else {
        payload.sellYear = interestFields.sellYear ? Number(interestFields.sellYear) : undefined;
        payload.sellMake = interestFields.sellMake || undefined;
        payload.sellModel = interestFields.sellModel || undefined;
        payload.sellTrim = interestFields.sellTrim || undefined;
        payload.sellMileage = interestFields.sellMileage
          ? Number(interestFields.sellMileage)
          : undefined;
        payload.sellCondition = interestFields.sellCondition || 'used';
      }
    }

    try {
      const response = await api.post('/auth/register', payload);

      if (response.data.success) {
        const { user } = response.data.data;
        dispatch(loginSuccess({ user }));

        if (isAnalyticsAllowed() && typeof window.gtag === 'function') {
          window.gtag('event', 'conversion', {
            send_to: 'AW-18028229435/rXqnCILtlo0cELvmw5RD',
          });
        }

        try {
          const wishlistIds = await getWishlist();
          dispatch(setWishlist(wishlistIds));
          const pendingCarId = localStorage.getItem('pendingWishlistCarId');
          if (pendingCarId && !wishlistIds.some((id) => String(id) === String(pendingCarId))) {
            try {
              await addToWishlist(pendingCarId);
              const updatedWishlistIds = await getWishlist();
              dispatch(setWishlist(updatedWishlistIds));
            } catch (wishlistErr) {
              console.error('Error adding pending car to wishlist:', wishlistErr);
            }
            localStorage.removeItem('pendingWishlistCarId');
          }
        } catch (wishlistLoadErr) {
          console.error('Error loading wishlist after registration:', wishlistLoadErr);
        }

        setTimeout(() => {
          navigate(redirectPath || '/', { replace: true });
        }, 300);
      }
    } catch (err) {
      console.error('Registration error:', err);
      setError(err.response?.data?.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterComplete = (e) => {
    e.preventDefault();
    finishRegistration(true);
  };

  const handleRegisterSkipInterest = () => {
    finishRegistration(false);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setLoading(true);

    try {
      const response = await api.post('/auth/login', {
        email: email.trim(),
        password,
        twoFactorCode: requiresTwoFactor ? twoFactorCode : undefined,
      });

      // Check if 2FA is required
      if (response.data.requiresTwoFactor && !requiresTwoFactor) {
        setRequiresTwoFactor(true);
        setLoading(false);
        return;
      }

      if (response.data.success) {
        const { user } = response.data.data;
        dispatch(loginSuccess({ user }));
        
        try {
          const wishlistIds = await getWishlist();
          dispatch(setWishlist(wishlistIds));
          
          const pendingCarId = localStorage.getItem('pendingWishlistCarId');
          if (pendingCarId && !wishlistIds.some(id => String(id) === String(pendingCarId))) {
            try {
              await addToWishlist(pendingCarId);
              const updatedWishlistIds = await getWishlist();
              dispatch(setWishlist(updatedWishlistIds));
            } catch (error) {
              console.error('Error adding pending car to wishlist:', error);
            }
            localStorage.removeItem('pendingWishlistCarId');
          }
        } catch (error) {
          console.error('Error loading wishlist after login:', error);
        }
        
        setTimeout(() => {
          const redirectPath = searchParams.get('redirect');
          navigate(redirectPath || '/', { replace: true });
        }, 300);
      }
    } catch (err) {
      console.error('Login error:', err);
      setError(err.response?.data?.message || 'Login failed. Please check your credentials.');
      // Reset 2FA state on error
      if (err.response?.status === 401) {
        setRequiresTwoFactor(false);
        setTwoFactorCode('');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated && user) {
      navigate(redirectPath || '/', { replace: true });
    }
  }, [isAuthenticated, user, redirectPath, navigate]);

  const handleBack = () => {
    if (step === 'register-details') {
      setStep('register');
    } else if (step === 'register') {
      setStep('email');
      setPassword('');
      setFirstName('');
      setLastName('');
      setPhone('');
    } else if (step === 'forgot-password' || step === 'reset-password') {
      setStep('login');
      setResetToken('');
      setNewPassword('');
      setConfirmPassword('');
    } else {
      setStep('email');
      setPassword('');
      setFirstName('');
      setLastName('');
      setPhone('');
    }
    setError('');
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      setLoading(false);
      return;
    }

    try {
      const response = await api.post('/auth/forgot-password', { email });
      if (response.data.success) {
        setResetToken('');
        setStep('reset-password');
      }
    } catch (err) {
      console.error('Forgot password error:', err);
      setError(err.response?.data?.message || 'Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!resetToken) {
      setError('Please enter the reset token from your email');
      setLoading(false);
      return;
    }

    const passwordValidation = validatePassword(newPassword);
    if (!passwordValidation.isValid) {
      setError('Password does not meet requirements');
      setLoading(false);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    try {
      const response = await api.post('/auth/reset-password', {
        email,
        token: resetToken,
        newPassword,
      });

      if (response.data.success) {
        setPassword('');
        setResetToken('');
        setNewPassword('');
        setConfirmPassword('');
        setError('');
        setStep('login');
        setSuccessMessage('Password updated. Sign in with your new password below.');
      }
    } catch (err) {
      console.error('Reset password error:', err);
      setError(err.response?.data?.message || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const passwordValidation = password ? validatePassword(password) : null;

  return (
    <div className="auth-page">
      <div className="auth-content">
        {/* Logo */}
        <div className="auth-logo-section">
          <Link to="/" className="auth-logo-link">
            <BrandLogo className="auth-brand-logo" alt="MyCarSlot" />
          </Link>
        </div>

        {/* Card */}
        <div className={`auth-card${step === 'register-details' ? ' auth-card-wide' : ''}`}>
          <div className="auth-card-body">
            {/* Email Step */}
            {step === 'email' && (
              <>
                <h1 className="auth-title">{t('auth.welcome')}</h1>
                <p className="auth-subtitle">{t('auth.enterEmailToStart')}</p>

                {error && (
                  <div className="auth-error">
                    {error}
                  </div>
                )}

                <form onSubmit={handleEmailContinue} className="auth-form">
                  <div className="auth-field">
                    <label className="auth-label">{t('auth.email')}</label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={t('auth.emailPlaceholder')}
                      className="auth-input"
                      required
                      autoFocus
                      disabled={loading}
                    />
                  </div>

                  <button type="submit" className="auth-btn" disabled={loading}>
                    {loading ? t('auth.checking') : t('auth.continue')}
                  </button>
                </form>
              </>
            )}

            {/* Register Step */}
            {step === 'register' && (
              <>
                <div className="auth-header">
                  <button onClick={handleBack} className="auth-back-btn" disabled={loading}>
                    <ArrowLeft size={24} />
                  </button>
                  <div>
                    <h1 className="auth-title">{t('auth.createAccount')}</h1>
                    <p className="auth-subtitle">{t('auth.completeProfile')}</p>
                  </div>
                </div>

                {error && (
                  <div className="auth-error">
                    {error}
                  </div>
                )}

                <form onSubmit={handleRegisterContinue} className="auth-form">
                  <div className="auth-field">
                    <label className="auth-label">{t('auth.email')}</label>
                    <input
                      type="email"
                      value={email}
                      disabled
                      className="auth-input auth-input-disabled"
                    />
                  </div>

                  <div className="auth-row">
                    <div className="auth-field">
                      <label className="auth-label">{t('auth.firstName')}</label>
                      <input
                        type="text"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        placeholder="John"
                        className="auth-input"
                        required
                        autoFocus
                        disabled={loading}
                      />
                    </div>
                    <div className="auth-field">
                      <label className="auth-label">{t('auth.lastName')}</label>
                      <input
                        type="text"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        placeholder="Doe"
                        className="auth-input"
                        required
                        disabled={loading}
                      />
                    </div>
                  </div>

                  <div className="auth-field">
                    <label className="auth-label">{t('auth.phone')}</label>
                    <input
                      type="tel"
                      value={phone}
                      onChange={handlePhoneChange}
                      placeholder="(555) 555-5555"
                      maxLength={14}
                      className="auth-input"
                      required
                      disabled={loading}
                    />
                  </div>

                  <div className="auth-field">
                    <label className="auth-label">{t('auth.password')}</label>
                    <div className="auth-input-wrapper">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder={t('auth.createPassword')}
                        className="auth-input auth-input-password"
                        required
                        minLength={8}
                        disabled={loading}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="auth-toggle-btn"
                        disabled={loading}
                      >
                        {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                      </button>
                    </div>
                    {password && (
                      <div className="auth-requirements">
                        {[
                          { key: 'minLength', label: '8+ chars' },
                          { key: 'hasNumber', label: 'Number' },
                          { key: 'hasLetter', label: 'Letter' },
                          { key: 'noSpaces', label: 'No spaces' }
                        ].map(({ key, label }) => (
                          <div key={key} className={`auth-requirement ${passwordValidation[key] ? 'valid' : ''}`}>
                            <CheckCircle2 size={14} />
                            <span>{label}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <button type="submit" className="auth-btn" disabled={loading}>
                    {loading ? t('auth.checking') : 'Continue'}
                  </button>
                </form>
              </>
            )}

            {step === 'register-details' && (
              <>
                <div className="auth-header">
                  <button onClick={handleBack} className="auth-back-btn" disabled={loading}>
                    <ArrowLeft size={24} />
                  </button>
                  <div>
                    <h1 className="auth-title">Interested In</h1>
                    <p className="auth-subtitle">
                      Add your address and what you&apos;re looking for — highly recommended for better matches.
                    </p>
                  </div>
                </div>

                {error && <div className="auth-error">{error}</div>}

                <RegisterInterestStep
                  interestType={interestType}
                  onInterestTypeChange={setInterestType}
                  address={addressFields}
                  onAddressChange={setAddressFields}
                  interest={interestFields}
                  onInterestChange={setInterestFields}
                  loading={loading}
                  onSubmit={handleRegisterComplete}
                  onSkip={handleRegisterSkipInterest}
                />
              </>
            )}

            {/* Login Step */}
            {step === 'login' && (
              <>
                <div className="auth-header">
                  <button onClick={handleBack} className="auth-back-btn" disabled={loading}>
                    <ArrowLeft size={24} />
                  </button>
                  <div>
                    <h1 className="auth-title">{t('auth.welcomeBack')}</h1>
                    <p className="auth-subtitle">{t('auth.signInToAccount')}</p>
                  </div>
                </div>

                {successMessage && (
                  <div className="auth-success" role="status">
                    {successMessage}
                  </div>
                )}

                {error && (
                  <div className="auth-error">
                    {error}
                  </div>
                )}

                <form onSubmit={handleLogin} className="auth-form">
                  {!requiresTwoFactor ? (
                    <>
                      <div className="auth-field">
                        <label className="auth-label">{t('auth.email')}</label>
                        <input
                          type="email"
                          value={email}
                          disabled
                          className="auth-input auth-input-disabled"
                        />
                      </div>

                      <div className="auth-field">
                        <label className="auth-label">{t('auth.password')}</label>
                        <div className="auth-input-wrapper">
                          <input
                            type={showPassword ? 'text' : 'password'}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder={t('auth.enterPassword')}
                            className="auth-input auth-input-password"
                            required
                            autoFocus
                            disabled={loading}
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="auth-toggle-btn"
                            disabled={loading}
                          >
                            {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                          </button>
                        </div>
                      </div>

                      <div className="auth-actions">
                        <label className="auth-checkbox-label">
                          <input
                            type="checkbox"
                            checked={keepSignedIn}
                            onChange={(e) => setKeepSignedIn(e.target.checked)}
                            className="auth-checkbox"
                            disabled={loading}
                          />
                          <span>{t('auth.keepSignedIn')}</span>
                        </label>
                        <button
                          type="button"
                          onClick={() => setStep('forgot-password')}
                          className="auth-link"
                          disabled={loading}
                        >
                          {t('auth.forgotPassword')}
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="auth-field">
                        <label className="auth-label">{t('auth.twoFactorCode')}</label>
                        <input
                          type="text"
                          value={twoFactorCode}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                            setTwoFactorCode(value);
                            setError('');
                          }}
                          placeholder={t('auth.enter6DigitCode')}
                          className="auth-input"
                          required
                          autoFocus
                          disabled={loading}
                          maxLength={6}
                        />
                        <p className="auth-helper-text">
                          {t('auth.enterCodeFromApp')}
                        </p>
                      </div>
                    </>
                  )}

                  <button type="submit" className="auth-btn" disabled={loading}>
                    {loading ? (requiresTwoFactor ? t('auth.verifying') : t('auth.signingIn')) : (requiresTwoFactor ? t('auth.verify') : t('common.signIn'))}
                  </button>
                </form>
              </>
            )}

            {/* Forgot Password Step */}
            {step === 'forgot-password' && (
              <>
                <div className="auth-header">
                  <button onClick={handleBack} className="auth-back-btn" disabled={loading}>
                    <ArrowLeft size={24} />
                  </button>
                  <div>
                    <h1 className="auth-title">{t('auth.resetPassword')}</h1>
                    <p className="auth-subtitle">{t('auth.sendResetCode')}</p>
                  </div>
                </div>

                {error && (
                  <div className="auth-error">
                    {error}
                  </div>
                )}

                <form onSubmit={handleForgotPassword} className="auth-form">
                  <div className="auth-field">
                    <label className="auth-label">{t('auth.email')}</label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={t('auth.emailPlaceholder')}
                      className="auth-input"
                      required
                      autoFocus
                      disabled={loading}
                    />
                  </div>

                  <button type="submit" className="auth-btn" disabled={loading}>
                    {loading ? t('auth.sending') : t('auth.sendResetCodeButton')}
                  </button>
                </form>
              </>
            )}

            {/* Reset Password Step */}
            {step === 'reset-password' && (
              <>
                <div className="auth-header">
                  <button onClick={handleBack} className="auth-back-btn" disabled={loading}>
                    <ArrowLeft size={24} />
                  </button>
                  <div>
                    <h1 className="auth-title">{t('auth.newPassword')}</h1>
                    <p className="auth-subtitle">{t('auth.enterCodeAndPassword')}</p>
                  </div>
                </div>

                {error && (
                  <div className="auth-error">
                    {error}
                  </div>
                )}

                <form onSubmit={handleResetPassword} className="auth-form">
                  <div className="auth-field">
                    <label className="auth-label">{t('auth.email')}</label>
                    <input
                      type="email"
                      value={email}
                      disabled
                      className="auth-input auth-input-disabled"
                    />
                  </div>

                  <div className="auth-field">
                    <label className="auth-label">{t('auth.resetCode')}</label>
                    <input
                      type="text"
                      value={resetToken}
                      onChange={(e) => setResetToken(e.target.value)}
                      placeholder={t('auth.resetCodePlaceholder')}
                      maxLength={6}
                      className="auth-input auth-input-code"
                      required
                      autoFocus
                      disabled={loading}
                    />
                  </div>

                  <div className="auth-field">
                    <label className="auth-label">New Password</label>
                    <div className="auth-input-wrapper">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="Create new password"
                        className="auth-input auth-input-password"
                        required
                        minLength={8}
                        disabled={loading}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="auth-toggle-btn"
                        disabled={loading}
                      >
                        {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                      </button>
                    </div>
                    {newPassword && (
                      <div className="auth-requirements">
                        {[
                          { key: 'minLength', label: '8+ chars' },
                          { key: 'hasNumber', label: 'Number' },
                          { key: 'hasLetter', label: 'Letter' },
                          { key: 'noSpaces', label: 'No spaces' }
                        ].map(({ key, label }) => (
                          <div key={key} className={`auth-requirement ${validatePassword(newPassword)[key] ? 'valid' : ''}`}>
                            <CheckCircle2 size={14} />
                            <span>{label}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="auth-field">
                    <label className="auth-label">{t('auth.confirmPassword')}</label>
                    <div className="auth-input-wrapper">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder={t('auth.confirmPasswordPlaceholder')}
                        className="auth-input auth-input-password"
                        required
                        minLength={8}
                        disabled={loading}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="auth-toggle-btn"
                        disabled={loading}
                      >
                        {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                      </button>
                    </div>
                  </div>

                  <button type="submit" className="auth-btn" disabled={loading}>
                    {loading ? t('auth.resetting') : t('auth.resetPasswordButton')}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Auth;